import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { DatePickerControl } from 'components/_basic/Form/DatePicker/DatePickerControl';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import { STATUS_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { STATUS_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { STATUS_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { STATUS_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { capitalizeOnlyFirstLetter } from 'helpers/capitalizeOnlyFirstLetter';
import { getDayJsDate } from 'helpers/formatDates';
import { concatWithDot } from 'helpers/string';

import { TIMELINE_ROW_NAMES } from '../utils/form';

export const DevelopmentTimelineRow: FC<{ name: string; disabled?: boolean }> = memo(({ name, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const { control, getValues } = useFormContext();
  const fromDateValue = getValues(concatWithDot(name, TIMELINE_ROW_NAMES.START_DATE)) || undefined;
  const toDateValue = getValues(concatWithDot(name, TIMELINE_ROW_NAMES.END_DATE)) || undefined;

  const getChosenStatusOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return STATUS_OPTIONS_DEU;

      case 'uk':
        return STATUS_OPTIONS_UA;

      case 'es':
        return STATUS_OPTIONS_ES;

      default:
        return STATUS_OPTIONS;
    }
  };

  return (
    <BorderedCard>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInputControl
            control={control}
            id={concatWithDot(name, TIMELINE_ROW_NAMES.STAGE)}
            name={concatWithDot(name, TIMELINE_ROW_NAMES.STAGE)}
            label={t('stage')}
            disabled={disabled}
            maxLength={50}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePickerControl
            control={control}
            id={concatWithDot(name, TIMELINE_ROW_NAMES.START_DATE)}
            name={concatWithDot(name, TIMELINE_ROW_NAMES.START_DATE)}
            label={capitalizeOnlyFirstLetter(t('from'))}
            disabled={disabled}
            dateFormat="MMMM YYYY"
            views={['month', 'year']}
            maxDate={getDayJsDate(toDateValue)}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePickerControl
            control={control}
            id={concatWithDot(name, TIMELINE_ROW_NAMES.END_DATE)}
            name={concatWithDot(name, TIMELINE_ROW_NAMES.END_DATE)}
            label={capitalizeOnlyFirstLetter(t('to'))}
            disabled={disabled}
            dateFormat="MMMM YYYY"
            views={['month', 'year']}
            minDate={getDayJsDate(fromDateValue)}
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteControl
            control={control}
            label={t('status')}
            id={concatWithDot(name, TIMELINE_ROW_NAMES.STATUS)}
            name={concatWithDot(name, TIMELINE_ROW_NAMES.STATUS)}
            options={getChosenStatusOptions(i18n.language)}
            defaultValue={getChosenStatusOptions(i18n.language)[0]}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
